import {FilterValue} from 'antd/es/table/interface';
import {showCommunicationError} from 'helpers/alerts';
import {action, computed, observable, runInAction} from 'mobx';
import {CalendarState} from 'store/common/CalendarState';
import {TableState} from 'store/common/TableState';
import {ApiReservationFindByGetRequest, ReservationDto} from '../../../../../PRMGApiClient';
import {Api} from '../../../../../PRMGApiClient/ApiClient';
import {Mixin} from 'ts-mixer';
import {Task} from 'views/calendar/calendarBox/gantt/task';
import {CalendarItem} from 'views/calendar/calendarBox/CalendarBoxState';
import moment from 'moment';

type TaskFilters = Pick<
  ApiReservationFindByGetRequest,
  'filtersCarId' | 'filtersCarModelIds' | 'filtersCustomerIds' | 'filtersFrom' | 'filtersTo'
>;

export class ReservationsState extends Mixin(TableState<ReservationDto, TaskFilters>, CalendarState) {
  constructor() {
    super();
  }

  @action
  loadTableItems() {
    this.isLoading = true;
    Api.Reservation.apiReservationFindByGet({
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      sortPropertyName: this.sortPropertyName,
      sortDirection: this.sortDirection,
      filtersCarId: this.filtersCarId,
      ...this.filters,
    })
      .then((res) => {
        runInAction(() => {
          this.items = res.content?.items || [];
          this.total = res.content?.total || 0;
          res.content?.items && this.createTasksList(res.content.items);
        });
      })
      .catch(showCommunicationError)
      .finally(() => {
        this.isLoading = false;
      });
  }

  @action
  loadCalendarItems() {
    this.isLoading = true;
    this.setDefaultDateRangeCalendar();

    Api.Reservation.apiReservationFindByGet({
      filtersFrom: this.filtersDateRangeStartCalendar,
      filtersTo: this.filtersDateRangeEndCalendar,
      pageSize: 1000,
    })
      .then((res) => {
        runInAction(() => {
          this.items = res.content?.items || [];
        });
      })
      .catch(showCommunicationError)
      .finally(() => {
        this.isLoading = false;
      });
  }

  setFilters(filters: Record<keyof ReservationDto, FilterValue | null>) {
    this.filters.filtersFrom = this.getDate(filters.begins);
    this.filters.filtersTo = this.getDate(filters.ends);
    this.filters.filtersCustomerIds = (filters.customer as number[]) ?? undefined;
    this.filters.filtersCarModelIds = filters.car
      ?.filter((item) => !(item as string).includes('brand'))
      .map((item) => Number((item as string).replace(/^\D+/g, '')));

    this.loadTableItems();
  }

  createTasksList(items: ReservationDto[]) {
    this.calendarListTasks = new Map<string, Array<ReservationDto | CalendarItem>>();
    items.forEach((i) => {
      if (i.car?.plateNumber) {
        const elements = this.calendarListTasks.get(i.car?.plateNumber ?? '');
        const index = elements ? this.calendarListTasks.size - 1 : this.calendarListTasks.size;
        elements
          ? this.calendarListTasks.set(i.car.plateNumber, [...elements, this.mapGanttTaskIrem(i, index)])
          : this.calendarListTasks.set(i.car.plateNumber, [this.mapGanttTaskIrem(i, index)]);
      }
    });
  }

  @observable
  calendarListTasks = new Map<string, Array<ReservationDto | CalendarItem>>();

  @computed
  get calendarTaskList(): Task[] {
    return Array.from(this.calendarListTasks.values()).flat() as unknown as Task[];
  }

  mapGanttTaskIrem(item: ReservationDto, index: number): CalendarItem {
    const ganttTimeFormat = 'YYYY-MM-DD';
    return {
      id: item.id?.toString() ?? '1',
      carId: item.car?.carId ?? 1,
      index: index,
      name: item.car?.plateNumber ?? '',
      start: moment(item.begins).format(ganttTimeFormat),
      end: moment(item.ends).format(ganttTimeFormat),
      progress: item.ends && item.ends < new Date() ? 100 : item.begins && item.begins <= new Date() ? 50 : 0,
      custom_class: '',
      plateNumber: item.car?.plateNumber ?? '',
    };
  }
}
